/**
 * Resilient cache for provider portal.
 *
 * Rules (per user spec):
 *   - localStorage only (no IndexedDB / no service worker yet)
 *   - Versioned envelopes with TTL
 *   - NEVER persist financial fields (amount, balance, profit, commission, fee, currency_to)
 *   - NEVER persist tokens or password hashes
 *   - Read-only fallback: cache is shown when network is down
 *   - Auto-purge on logout
 *
 * Future-ready: the public `getCached/setCached` shape is stable so we can swap
 * to IndexedDB + Service Worker + BackgroundSync without changing call sites.
 */

const CACHE_VERSION = 1;
const TTL_MS = 10 * 60 * 1000; // 10 minutes

const SENSITIVE_KEYS = new Set([
  'amount', 'balance', 'profit', 'commission', 'commission_amount',
  'commission_pct', 'fee', 'fees', 'provider_cost', 'selling_price',
  'currency_to', // we keep currency_from + route fields (purely operational)
  'password', 'access_token', 'token',
]);

/** Remove sensitive fields from any value, deep. */
const sanitize = (val) => {
  if (val == null) return val;
  if (Array.isArray(val)) return val.map(sanitize);
  if (typeof val === 'object') {
    const out = {};
    Object.keys(val).forEach((k) => {
      if (SENSITIVE_KEYS.has(k)) return;
      out[k] = sanitize(val[k]);
    });
    return out;
  }
  return val;
};

const keyFor = (scope, userId) => `aircom:cache:${CACHE_VERSION}:${userId}:${scope}`;

export const setCached = (scope, userId, data) => {
  if (!userId || !scope) return;
  try {
    const envelope = {
      version: CACHE_VERSION,
      saved_at: Date.now(),
      ttl_ms: TTL_MS,
      data: sanitize(data),
    };
    localStorage.setItem(keyFor(scope, userId), JSON.stringify(envelope));
  } catch (err) {
    // Quota or serialization issue — degrade silently. Cache is best-effort.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[resilientCache] setCached failed', err);
    }
  }
};

export const getCached = (scope, userId) => {
  if (!userId || !scope) return null;
  try {
    const raw = localStorage.getItem(keyFor(scope, userId));
    if (!raw) return null;
    const env = JSON.parse(raw);
    if (env.version !== CACHE_VERSION) return null;
    if (Date.now() - env.saved_at > env.ttl_ms) return null; // expired
    return { data: env.data, savedAt: env.saved_at, age: Date.now() - env.saved_at };
  } catch (err) {
    // Corrupt envelope — treat as cache-miss.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[resilientCache] getCached parse failed', err);
    }
    return null;
  }
};

export const clearCache = (userId) => {
  if (!userId) {
    // Clear everything cached by this app
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith(`aircom:cache:`)) localStorage.removeItem(k);
    });
    return;
  }
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith(`aircom:cache:${CACHE_VERSION}:${userId}:`)) localStorage.removeItem(k);
  });
};

/**
 * Wrap a fetcher with cache-fallback semantics.
 *
 * @param scope    cache key suffix (e.g. 'provider:queues')
 * @param userId   the current user id
 * @param fetcher  async () => fresh data
 * @returns { data, fromCache, age, error }
 */
export const fetchWithFallback = async (scope, userId, fetcher) => {
  try {
    const data = await fetcher();
    setCached(scope, userId, data);
    return { data, fromCache: false, age: 0, error: null };
  } catch (error) {
    const cached = getCached(scope, userId);
    if (cached) return { data: cached.data, fromCache: true, age: cached.age, error };
    return { data: null, fromCache: false, age: 0, error };
  }
};
